import React, { useRef } from "react"
import PropTypes from "prop-types"
import { Search, X } from "../Icon"
import { cn } from "../../../lib/utils"

/**
 * SearchInput — compact table-toolbar search control.
 *
 * Designed to sit flush alongside Button, FilterChip, and ExportMenu
 * at 28px height with matching border/hover tokens.
 *
 * Props:
 *   value        {string}   — controlled value
 *   onChange     {function} — called with the native change event
 *   onClear      {function} — optional; called when the clear button is
 *                             clicked. Falls back to firing onChange("").
 *   placeholder  {string}   — defaults to "Search..."
 *   className    {string}   — extra classes merged onto the outer wrapper
 *   inputRef     {Ref}      — forwarded ref for the <input> element
 *   ...rest      — spread onto the <input> (aria-label, data-testid, etc.)
 */
const SearchInput = ({
  value,
  onChange,
  onClear,
  placeholder = "Search...",
  className,
  inputRef: externalRef,
  ...rest
}) => {
  const internalRef = useRef(null)
  const inputRef = externalRef || internalRef

  const handleClear = () => {
    if (onClear) {
      onClear()
    } else {
      const nativeInput = inputRef.current
      if (nativeInput) {
        const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
          window.HTMLInputElement.prototype,
          "value",
        ).set
        nativeInputValueSetter.call(nativeInput, "")
        nativeInput.dispatchEvent(new Event("input", { bubbles: true }))
        onChange({ target: { value: "" } })
      } else {
        onChange({ target: { value: "" } })
      }
    }
    inputRef.current?.focus()
  }

  const hasValue = Boolean(value)

  return (
    <div className={cn("relative flex items-center w-full", className)}>
      {/* Search icon — muted, same color as toolbar button icons */}
      <Search
        size={12}
        className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--gray-dark)]"
        aria-hidden="true"
      />

      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className={cn(
          "w-full h-7",
          "pl-7",
          hasValue ? "pr-7" : "pr-2.5",
          // Ghost input — no border, transparent bg, borderless design
          "rounded-[var(--radius,6px)]",
          "border-none",
          "bg-transparent",
          // Typography — matches toolbar density
          "text-[var(--font-xs)] text-[var(--slate)]",
          "placeholder:text-[var(--gray-dark)] placeholder:font-normal",
          "outline-none",
          // Hover — subtle bg tint only
          "hover:bg-[var(--green-tint-light)]",
          // Transition
          "transition-[background-color] duration-150",
          "font-[inherit]",
        )}
        {...rest}
      />

      {/* Clear button — appears when value present */}
      {hasValue && (
        <button
          type="button"
          onClick={handleClear}
          aria-label="Clear search"
          className={cn(
            "absolute right-1.5 top-1/2 -translate-y-1/2",
            "flex items-center justify-center",
            "h-4 w-4",
            "rounded-sm",
            "border-none bg-transparent",
            "text-[var(--gray-dark)]",
            "hover:bg-[var(--green-tint-light)] hover:text-[var(--slate)]",
            "cursor-pointer",
            "transition-colors duration-100",
          )}
        >
          <X size={10} />
        </button>
      )}
    </div>
  )
}

SearchInput.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  onClear: PropTypes.func,
  placeholder: PropTypes.string,
  className: PropTypes.string,
  inputRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  ]),
}

export default SearchInput
